'use client';

import {
    LocWHCookies,
    LocaleType,
    SearchProps,
} from '@/src/types/pages/search';

import {
    CATEGORY,
    CONTENTSTACK,
    DYNAMIC_LANDING_PAGE,
    ENV_NONPROD,
    ENV_PROD,
    ERROR_PAGE,
    ERROR_PAGE_404,
    ERROR_PAGE_500,
    REACT,
    SEARCH,
} from './constants';

const getPageType = (pageName: string) => {
    if ([ERROR_PAGE_404, ERROR_PAGE_500].includes(pageName)) {
        return ERROR_PAGE;
    } else if (pageName.includes(CATEGORY)) {
        return CATEGORY;
    } else {
        return SEARCH;
    }
};

/**
 * Function to populate the window.digitalData with the common search page properties
 * @param lang
 * @param locWHCookies
 * @param sessionStatus
 * @param site
 */
export function populateCommonSearchDigitalData(
    lang: LocaleType,
    locWHCookies: LocWHCookies,
    sessionStatus: 'Authenticated' | 'Unauthenticated' | undefined,
    site: string,
    pageName: string = SEARCH,
    pageCrumbs: (string | undefined)[] | undefined,
) {
    const [siteLang, siteCountry] = lang.split('-');
    const siteStore = site.slice(-2).toLowerCase();
    const siteReleaseEnv =
        process?.env
            ?.NEXT_PUBLIC_CONTENT_ENTRY_ENVIRONMENT_CLIENT_CONFIGURATION &&
        process?.env?.NEXT_PUBLIC_CONTENT_ENTRY_ENVIRONMENT_CLIENT_CONFIGURATION.toLowerCase() ===
            ENV_PROD
            ? ENV_PROD
            : ENV_NONPROD;

    if (window) {
        window.digitalData = {
            ...window.digitalData,
            authStatus: sessionStatus,
            deliveryZip: locWHCookies.deliveryZip,
            localWH: locWHCookies.localWH,
            pageCMS: CONTENTSTACK,
            pageCrumbs,
            pageName,
            pagePlatform: REACT,
            pageType: getPageType(pageName),
            siteCountry,
            siteLang: `${siteLang}-${siteCountry?.toUpperCase()}`,
            siteReleaseEnv,
            siteStore,
            // entity is in the format costco-b2c-us
            entity:
                'costco-' + siteStore.split('').join('2') + '-' + siteCountry,
            pageCategories: window.digitalData?.pageCategories ?? [],
        };
    }
}

/**
 * Function to populate the window.digitalData with the fusion properties from LW response
 * @param fusionProps
 * @param noResults
 */
export function populateFusionDigitalData(
    searchProps: SearchProps,
    pageName: string | undefined,
) {
    if (window) {
        window.digitalData = {
            ...window.digitalData,
            ...searchProps,
            products: [],
            pageName,
            pageCategories: window.digitalData?.pageCategories ?? [],
        };
    }
}

// ---------------------------------------------------------------------------
// DLP (Dynamic Landing Page) analytics
// ---------------------------------------------------------------------------

/** Shape of a single product entry in the DLP digitalData products array */
export type DLPProduct = {
    pid: string;
    sku: string;
};

/**
 * Populate window.digitalData with DLP-specific analytics fields.
 * Must only be called client-side (inside a useEffect or similar).
 */
export function populateDLPDigitalData({
    lang,
    sessionStatus,
    deliveryZip,
    pageTitle,
    products,
}: {
    lang: string;
    sessionStatus: 'Authenticated' | 'Unauthenticated' | undefined;
    deliveryZip: string;
    pageTitle: string;
    products: DLPProduct[];
}) {
    const site = process.env.NEXT_PUBLIC_SITE ?? '';
    const [siteLangCode, siteCountry] = lang.split('-');
    const siteStore = site.slice(-2).toLowerCase();
    const siteReleaseEnv =
        process.env.NEXT_PUBLIC_CONTENT_ENTRY_ENVIRONMENT_CLIENT_CONFIGURATION?.toLowerCase() ===
        ENV_PROD
            ? ENV_PROD
            : ENV_NONPROD;

    if (window) {
        window.digitalData = {
            ...window.digitalData,
            authStatus: sessionStatus,
            deliveryZip,
            pageCms: CONTENTSTACK,
            pageName: `${DYNAMIC_LANDING_PAGE} | ${pageTitle}`,
            pagePlatform: REACT,
            pageType: DYNAMIC_LANDING_PAGE,
            products,
            siteCountry,
            siteLang: `${siteLangCode}-${siteCountry?.toUpperCase()}`,
            siteReleaseEnv,
            siteStore,
        };
    }
}
